// Shared UI pieces for GhostWriter Events (Phase 10 · Step 2).
// Styling lives in styles.css; these components only add structure and behaviour.
import { useEffect, useId, useRef, useState } from "react";
import { safeHref } from "./lib/links.js";

/* ───────────── Phone width (matches the 720px breakpoint in styles.css) ───────────── */
export function useMedia(query) {
  const get = () => typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false;
  const [match, setMatch] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, [query]);
  return match;
}
export const useIsPhone = () => useMedia("(max-width: 720px)");

/* ───────────── Status chips ─────────────
   Brand rule: Draft / Pending / Considering = vellum · Signed / Paid / Approved = ink ·
   Overdue / Declined = oxblood (the screen's one alert). */
const STATUS_CLASS = {
  // neutral (vellum)
  "Draft": "tag-neutral", "Pending": "tag-neutral", "Considering": "tag-neutral", "Sent": "tag-neutral",
  "Negotiating": "tag-neutral", "Incomplete": "tag-neutral", "In progress": "tag-neutral", "Upcoming": "tag-neutral",
  // done (ink)
  "Signed": "tag-done", "Paid": "tag-done", "Approved": "tag-done", "Approved — ready to send": "tag-done",
  "Client approved": "tag-done", "Executed": "tag-done", "Complete": "tag-done", "Done": "tag-done",
  // alert (oxblood)
  "Overdue": "tag-alert", "Declined": "tag-alert", "Client declined": "tag-alert",
  // set aside (outline)
  "Rejected": "tag-outline"
};
export const statusClass = s => STATUS_CLASS[s] || "tag-neutral";
export function StatusTag({ status, children, className = "", ...rest }) {
  return <span className={"tag " + statusClass(status) + (className ? " " + className : "")} {...rest}>{children || status}</span>;
}

/* ───────────── Notes / callouts ───────────── */
export function Note({ children, tone, className = "", ...rest }) {
  const cls = "note" + (tone === "warn" ? " note-warn" : tone === "ok" ? " note-ok" : "") + (className ? " " + className : "");
  return <div className={cls} role={tone === "warn" ? "status" : undefined} {...rest}>{children}</div>;
}

/* ───────────── Thin ink meter ───────────── */
export function Meter({ value, label, className = "" }) {
  const v = Math.max(0, Math.min(100, Math.round(value || 0)));
  return (
    <div className={"meter " + className} role="progressbar" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <span style={{ width: v + "%" }} />
    </div>
  );
}

/* ───────────── Label + value pair ───────────── */
export function Meta({ label, children, className = "", style }) {
  return (
    <div className={"meta " + className} style={style}>
      <div className="label">{label}</div>
      <div className="meta-value">{children}</div>
    </div>
  );
}

/* ───────────── The one shared dialog (gap #7) ─────────────
   - announced to screen readers (role="dialog", aria-modal, labelled by its title)
   - moves keyboard focus inside on open and keeps it there (Tab / Shift-Tab wrap)
   - Escape closes; clicking the dimmed backdrop closes
   - returns focus to whatever opened it
   - full screen on phones (styles.css) */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const openDialogs = [];   // stack, so only the top dialog reacts to Escape/Tab

export function Dialog({ title, kicker, aside, onClose, width = 560, children, footer, className = "" }) {
  const id = useId();
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const node = ref.current;
    const opener = document.activeElement;
    openDialogs.push(node);
    const visible = el => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    const items = () => [...node.querySelectorAll(FOCUSABLE)].filter(visible);
    // First field in the body if there is one, otherwise the dialog itself.
    const first = [...node.querySelectorAll(".dialog-body " + FOCUSABLE)].filter(visible)
      .find(el => /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) || node;
    first.focus({ preventScroll: true });
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = e => {
      if (openDialogs[openDialogs.length - 1] !== node) return;
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); if (closeRef.current) closeRef.current(); return; }
      if (e.key !== "Tab") return;
      const list = items();
      if (!list.length) { e.preventDefault(); node.focus(); return; }
      const a = list[0], z = list[list.length - 1];
      if (e.shiftKey && (document.activeElement === a || document.activeElement === node)) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && (document.activeElement === z || !node.contains(document.activeElement))) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      const i = openDialogs.indexOf(node); if (i >= 0) openDialogs.splice(i, 1);
      if (!openDialogs.length) document.body.style.overflow = prevOverflow;
      if (opener && typeof opener.focus === "function" && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  return (
    <div className="dialog-backdrop" onMouseDown={e => { if (e.target === e.currentTarget && closeRef.current) closeRef.current(); }}>
      <div ref={ref} className={"dialog" + (className ? " " + className : "")} role="dialog" aria-modal="true" aria-labelledby={id + "-title"} tabIndex={-1}
        style={{ "--dialog-w": width + "px" }}>
        <div className="dialog-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            {kicker && <div className="label">{kicker}</div>}
            <h2 id={id + "-title"} className="dialog-title">{title}</h2>
          </div>
          {aside}
          <button type="button" className="btn btn-secondary dialog-close" onClick={() => closeRef.current && closeRef.current()}>Close</button>
        </div>
        <div className="dialog-body">{children}</div>
        {footer && <div className="dialog-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ───────────── Safe external link (gap #12) ─────────────
   http/https only, opens in a new tab without giving that page access back to this one.
   Anything that isn't a safe web address is shown as plain text. */
export function SafeLink({ href, children, className = "" }) {
  const url = safeHref(href);
  const label = children || href;
  if (!url) return <span className={className}>{label}</span>;
  return <a href={url} target="_blank" rel="noopener noreferrer nofollow" className={className}>{label}</a>;
}
